import { Box, Modal, Stack, Text } from '@mantine/core';
import { featureCollection, point } from '@turf/turf';
import { MapEvent } from 'mapbox-gl';
import { FC, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MapRef } from 'react-map-gl';

import { getAnswerCaptureCoordinates } from '../../utils/format-answer-capture-metadata';
import { CapturePositionMarker } from '../capture-position-marker/capture-position-marker';

import classes from './answer-capture-map-modal.module.css';

import {
  asFieldsFeatureCollectionFromFeatures,
  asGeoJsonFeature,
  hasFieldGeometry,
} from '@fields/data-access';
import { HarvestProofAnswer } from '@harvest-proof/data-access';
import { PfMap, PlotsLayer, zoomOnFeature } from '@map';

interface AnswerCaptureMapModalProps {
  answer: HarvestProofAnswer | null;
  opened: boolean;
  onClose: () => void;
}

export const AnswerCaptureMapModal: FC<AnswerCaptureMapModalProps> = ({
  answer,
  opened,
  onClose,
}) => {
  const { t } = useTranslation();
  const mapRef = useRef<MapRef>(null);
  const coordinates = answer ? getAnswerCaptureCoordinates(answer) : null;
  const fieldName = answer?.field.properties?.name ?? String(answer?.field.id ?? '');

  const fitMapView = useCallback(
    (map?: MapEvent['target']) => {
      if (!map || !coordinates) {
        return;
      }

      const capturePoint = point([coordinates.longitude, coordinates.latitude]);
      const field = answer?.field;

      if (field && hasFieldGeometry(field)) {
        zoomOnFeature({
          map,
          geojson: featureCollection([asGeoJsonFeature(field), capturePoint]),
          padding: 60,
        });
        return;
      }

      map.flyTo({
        center: [coordinates.longitude, coordinates.latitude],
        zoom: 16,
        duration: 0,
      });
    },
    [answer?.field, coordinates],
  );

  const handleLoad = useCallback(
    (event: MapEvent) => {
      fitMapView(event.target);
    },
    [fitMapView],
  );

  useEffect(() => {
    if (opened && coordinates) {
      fitMapView(mapRef.current?.getMap());
    }
  }, [coordinates, fitMapView, opened]);

  if (!answer || !coordinates) {
    return null;
  }

  const headingLabel =
    answer.device_heading != null
      ? `${Math.round(answer.device_heading)}°`
      : t('harvest-proof.detail.unavailable');

  return (
    <Modal
      onClose={onClose}
      opened={opened}
      size="md"
      title={t('harvest-proof.detail.captureMapTitle', { fieldName })}
    >
      <Stack gap="xs">
        <Box className={classes.mapWrapper}>
          <PfMap
            classnames={{ container: classes.mapInner }}
            onLoad={handleLoad}
            ref={mapRef}
          >
            {hasFieldGeometry(answer.field) ? (
              <PlotsLayer
                data={asFieldsFeatureCollectionFromFeatures([answer.field])}
              />
            ) : null}
            <CapturePositionMarker
              heading={answer.device_heading}
              latitude={coordinates.latitude}
              longitude={coordinates.longitude}
            />
          </PfMap>
        </Box>
        <Stack className={classes.legend} gap={2}>
          <Text size="sm">
            {t('harvest-proof.detail.captureGps')}:{' '}
            {coordinates.latitude.toFixed(5)}, {coordinates.longitude.toFixed(5)}
          </Text>
          <Text size="sm">
            {t('harvest-proof.detail.heading')}: {headingLabel}
          </Text>
          {answer.distance_meters != null ? (
            <Text size="sm">
              {t('harvest-proof.detail.distance')}:{' '}
              {Math.round(answer.distance_meters)} m
            </Text>
          ) : null}
        </Stack>
      </Stack>
    </Modal>
  );
};
